import { getSupabaseClient } from './supabase';
import type { OnboardingChatMessage, OnboardingChatResponse, SiteOnboardingDraft } from './onboarding-types';
import type { GenerationStreamRequest } from './draft-to-generation';

export interface LandingEditorApiConfig {
  apiOrigin: string;
}

export interface StartGenerationResponse {
  sessionId: string;
  plannedEvents?: unknown[];
}

export interface GenerationCompleteEvent {
  siteId: string;
  totalDuration?: number;
}

export interface GenerationErrorEvent {
  message: string;
  recoverable?: boolean;
}

export type GenerationStreamHandler = {
  onEvent?: (payload: unknown) => void;
  onComplete?: (event: GenerationCompleteEvent) => void;
  onError?: (event: GenerationErrorEvent) => void;
};

function resolveApiOrigin(): string {
  const raw = import.meta.env.VITE_LANDI_AGENTS_URL ?? import.meta.env.VITE_LANDING_EDITOR_API_ORIGIN ?? '';
  return raw.replace(/\/$/, '');
}

export function getLandingEditorApiOrigin(): string {
  return resolveApiOrigin();
}

export function isLandingEditorApiConfigured(): boolean {
  return resolveApiOrigin().length > 0;
}

async function buildAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const supabase = getSupabaseClient();
  if (!supabase) return headers;
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    console.error('[landing-editor-api] session read failed', error.message);
    return headers;
  }
  const token = data.session?.access_token;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const origin = resolveApiOrigin();
  if (!origin) {
    throw new Error('VITE_LANDI_AGENTS_URL is not configured');
  }
  const authHeaders = await buildAuthHeaders();
  const headers = {
    ...authHeaders,
    ...(init.headers instanceof Headers
      ? Object.fromEntries(init.headers.entries())
      : (init.headers as Record<string, string> | undefined)),
  };
  return fetch(`${origin}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });
}

/** POST `/api/onboarding/chat` — mirrors landing-editor runOnboardingChatTurn route. */
export async function postOnboardingChatTurn(input: {
  messages: OnboardingChatMessage[];
  draft: SiteOnboardingDraft;
}): Promise<OnboardingChatResponse> {
  const response = await apiFetch('/api/onboarding/chat', {
    method: 'POST',
    body: JSON.stringify({
      messages: input.messages,
      draft: input.draft,
    }),
  });

  if (!response.ok) {
    let message = `Onboarding chat failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* ignore parse errors */
    }
    throw new Error(message);
  }

  const body = (await response.json()) as OnboardingChatResponse;
  if (!body.assistantMessage || !body.draft) {
    throw new Error('Invalid onboarding chat response');
  }
  return body;
}

/** POST `/api/generate-stream` — returns session id for SSE subscription. */
export async function postStartGeneration(
  params: GenerationStreamRequest,
): Promise<StartGenerationResponse> {
  const response = await apiFetch('/api/generate-stream', {
    method: 'POST',
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    let message = `Generation start failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  const body = (await response.json()) as { sessionId?: string; plannedEvents?: unknown[] };
  if (!body.sessionId) {
    throw new Error('Generation session id missing from response');
  }
  return { sessionId: body.sessionId, plannedEvents: body.plannedEvents };
}

/** GET `/api/sites/:siteId/preview` — draft HTML for WebPanel (Bearer auth). */
export async function fetchSiteDraftPreview(siteId: string): Promise<string> {
  const response = await apiFetch(`/api/sites/${encodeURIComponent(siteId)}/preview`, {
    method: 'GET',
    headers: { Accept: 'text/html' },
  });

  if (!response.ok) {
    let message = `Draft preview failed (${response.status})`;
    if (response.status === 401) {
      message = 'Sign in required to load draft preview';
    } else if (response.status === 404) {
      message = 'Draft content not found yet';
    }
    throw new Error(message);
  }

  return response.text();
}

/** Subscribe to GET `/api/generate-stream/:sessionId` SSE until complete or error. */
export function subscribeGenerationStream(
  sessionId: string,
  handlers: GenerationStreamHandler,
): () => void {
  const origin = resolveApiOrigin();
  if (!origin) {
    handlers.onError?.({ message: 'VITE_LANDI_AGENTS_URL is not configured' });
    return () => undefined;
  }

  let source: EventSource | null = null;
  let cancelled = false;

  void (async () => {
    const supabase = getSupabaseClient();
    let accessToken: string | undefined;
    if (supabase) {
      const { data } = await supabase.auth.getSession();
      accessToken = data.session?.access_token;
    }

    if (cancelled) {
      return;
    }

    const params = new URLSearchParams();
    if (accessToken) {
      params.set('access_token', accessToken);
    }
    const query = params.toString();
    const url = `${origin}/api/generate-stream/${encodeURIComponent(sessionId)}${query ? `?${query}` : ''}`;
    source = new EventSource(url, { withCredentials: true });

    source.onmessage = (event: MessageEvent<string>) => {
      try {
        const payload = JSON.parse(event.data) as {
          type?: string;
          data?: Record<string, unknown>;
        };
        handlers.onEvent?.(payload);
        if (payload.type === 'complete') {
          const siteId = payload.data?.siteId;
          if (typeof siteId === 'string' && siteId) {
            handlers.onComplete?.({
              siteId,
              totalDuration:
                typeof payload.data?.totalDuration === 'number'
                  ? payload.data.totalDuration
                  : undefined,
            });
          }
          source?.close();
        }
        if (payload.type === 'error') {
          const message =
            typeof payload.data?.message === 'string'
              ? payload.data.message
              : 'Generation stream error';
          handlers.onError?.({
            message,
            recoverable: payload.data?.recoverable === true,
          });
          source?.close();
        }
      } catch (err) {
        console.error('[generation-stream] parse failed', err);
      }
    };

    source.onerror = () => {
      handlers.onError?.({ message: 'Generation stream disconnected', recoverable: true });
      source?.close();
    };
  })();

  return () => {
    cancelled = true;
    source?.close();
  };
}
